import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

/**
 * 149-as: Table layout hook — extension dari useTableColumnLayout.js (yang hanya
 * kelola width + order). Sekarang lengkap:
 *   - widths, order (existing)
 *   - hidden columns
 *   - density (compact | normal)
 *   - persistence per-user per-page via backend `/user-prefs/table/{pageKey}`
 *     dengan fallback localStorage saat offline / belum login.
 *
 * pageKey: identifier unik halaman (mis. "wo_monitoring", "ar_monitoring").
 * Preferensi user TIDAK mempengaruhi user lain (backend simpan per user_id).
 *
 * @returns {{
 *   columns: Array,                     // COLUMNS reordered, filtered by hidden
 *   allColumns: Array,                  // COLUMNS reordered incl hidden
 *   widths: Record<string,number>,
 *   getWidth: (key:string)=>number|undefined,
 *   setWidth: (key:string, px:number)=>void,
 *   moveColumn: (fromKey:string, toKey:string)=>void,
 *   isHidden: (key:string)=>boolean,
 *   toggleHidden: (key:string)=>void,
 *   hidden: Record<string,boolean>,
 *   density: "compact"|"normal",
 *   setDensity: (d:string)=>void,
 *   resetLayout: ()=>void,
 *   hasCustomLayout: boolean,
 *   loaded: boolean,                    // true setelah first fetch dari server
 * }}
 */
export function useTableLayoutV2(pageKey, defaultColumns, opts = {}) {
  const { minWidth = 60, maxWidth = 600 } = opts;
  const defaultOrder = useMemo(() => defaultColumns.map((c) => c.key), [defaultColumns]);
  const lsKey = `ens_tbl_v2:${pageKey}`;

  const bootFromLocal = () => {
    try {
      const raw = localStorage.getItem(lsKey);
      if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return null;
  };
  const initial = bootFromLocal() || {};

  const [order, setOrder] = useState(() => {
    const raw = Array.isArray(initial.order) ? initial.order : defaultOrder;
    const known = new Set(defaultOrder);
    const cleaned = raw.filter((k) => known.has(k));
    for (const k of defaultOrder) if (!cleaned.includes(k)) cleaned.push(k);
    return cleaned;
  });
  const [widths, setWidths] = useState(() => (initial.widths && typeof initial.widths === "object") ? initial.widths : {});
  const [hidden, setHidden] = useState(() => (initial.hidden && typeof initial.hidden === "object") ? initial.hidden : {});
  const [density, setDensityRaw] = useState(() => (initial.density === "compact" ? "compact" : "normal"));
  // 149-at: column pinning — { colKey: "left" | "right" }
  const [pinned, setPinned] = useState(() => (initial.pinned && typeof initial.pinned === "object") ? initial.pinned : {});
  const [loaded, setLoaded] = useState(false);

  // First-load: fetch dari backend (kalau belum ada di local, override).
  useEffect(() => {
    let alive = true;
    api.get(`/user-prefs/table/${pageKey}`).then(({ data }) => {
      if (!alive || !data) return;
      if (Array.isArray(data.order)) {
        const known = new Set(defaultOrder);
        const cleaned = data.order.filter((k) => known.has(k));
        for (const k of defaultOrder) if (!cleaned.includes(k)) cleaned.push(k);
        setOrder(cleaned);
      }
      if (data.widths && typeof data.widths === "object") setWidths(data.widths);
      if (data.hidden && typeof data.hidden === "object") setHidden(data.hidden);
      if (data.density === "compact" || data.density === "normal") setDensityRaw(data.density);
      if (data.pinned && typeof data.pinned === "object") setPinned(data.pinned);
    }).catch(() => { /* silent — fallback localStorage */ }).finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageKey]);

  // Persist ke local + server (debounced)
  useEffect(() => {
    const state = { order, widths, hidden, density, pinned };
    try { localStorage.setItem(lsKey, JSON.stringify(state)); } catch { /* ignore */ }
    if (!loaded) return; // jangan overwrite backend dgn state boot
    const t = setTimeout(() => {
      api.put(`/user-prefs/table/${pageKey}`, state).catch(() => { /* silent */ });
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order, widths, hidden, density, pinned, pageKey]);

  const byKey = useMemo(() => {
    const map = new Map();
    for (const c of defaultColumns) map.set(c.key, c);
    return map;
  }, [defaultColumns]);

  const allColumns = useMemo(() => order.map((k) => byKey.get(k)).filter(Boolean), [order, byKey]);
  const columns = useMemo(() => allColumns.filter((c) => !hidden[c.key]), [allColumns, hidden]);

  const setWidth = useCallback((key, px) => {
    setWidths((w) => ({ ...w, [key]: Math.max(minWidth, Math.min(maxWidth, Math.round(px))) }));
  }, [minWidth, maxWidth]);

  const moveColumn = useCallback((fromKey, toKey) => {
    if (fromKey === toKey) return;
    setOrder((prev) => {
      const from = prev.indexOf(fromKey);
      const to = prev.indexOf(toKey);
      if (from < 0 || to < 0) return prev;
      const next = prev.slice();
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  }, []);

  const toggleHidden = useCallback((key) => {
    setHidden((h) => {
      const next = { ...h };
      if (next[key]) delete next[key]; else next[key] = true;
      return next;
    });
  }, []);

  const setDensity = useCallback((d) => {
    setDensityRaw(d === "compact" ? "compact" : "normal");
  }, []);

  const resetLayout = useCallback(() => {
    setOrder(defaultOrder);
    setWidths({});
    setHidden({});
    setDensityRaw("normal");
    setPinned({});
  }, [defaultOrder]);

  const hasCustomLayout = order.join("|") !== defaultOrder.join("|")
    || Object.keys(widths).length > 0
    || Object.keys(hidden).length > 0
    || Object.keys(pinned).length > 0
    || density !== "normal";

  // 149-at: pin controls
  const setPin = useCallback((key, side) => {
    setPinned((p) => {
      const next = { ...p };
      if (!side || (side !== "left" && side !== "right")) delete next[key];
      else next[key] = side;
      return next;
    });
  }, []);
  const getPin = useCallback((key) => pinned[key], [pinned]);

  // Compute sticky offset per pinned column — cumulative width dari kolom sebelumnya
  // yang di-pin sama arah. Dipakai di render untuk `position: sticky; left/right: Npx`.
  const computeSticky = useCallback(() => {
    const map = new Map();
    // Left pins in visible order
    let leftAcc = 0;
    const leftPins = columns.filter((c) => pinned[c.key] === "left");
    for (const c of leftPins) {
      map.set(c.key, { side: "left", offset: leftAcc });
      leftAcc += (widths[c.key] || 140);
    }
    // Right pins in REVERSE visible order (last column = 0 from right)
    let rightAcc = 0;
    const rightPins = [...columns.filter((c) => pinned[c.key] === "right")].reverse();
    for (const c of rightPins) {
      map.set(c.key, { side: "right", offset: rightAcc });
      rightAcc += (widths[c.key] || 140);
    }
    return map;
  }, [columns, pinned, widths]);

  const getWidth = useCallback((key) => widths[key], [widths]);
  const isHidden = useCallback((key) => !!hidden[key], [hidden]);

  return {
    columns, allColumns, widths, getWidth, setWidth,
    moveColumn, hidden, isHidden, toggleHidden,
    density, setDensity,
    pinned, getPin, setPin, computeSticky,
    resetLayout, hasCustomLayout, loaded,
  };
}
